import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import axiosInstance from '../services/axiosInstance';
import GlobalTable from '../components/GlobalTable';
import { useLazyTable } from '../hooks/useLazyTable';
import MoveOrderDetailDialog from '../components/MoveOrderDetailDialog';
import MoveOrderPurchaseDialog from '../components/MoveOrderPurchaseDialog';
import PurchaseScanDialog from '../components/PurchaseScanDialog';
import SupplierAliasesDialog from '../components/SupplierAliasesDialog';
import { handleApiError, handleApiSuccess  } from '../components/apiErrorHandler';
import MoveOrderDialog from "../components/MoveOrderDialog";
import MoveOrderDeleteDialog from '../components/MoveOrderDeleteDialog';
import { useLocation } from 'react-router-dom';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { usePermission } from '../contexts/UserContext';

export default function MoveOrders() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isApprovalsView = new URLSearchParams(location.search).get('approvals') === '1';
  const focusMoveOrderId = searchParams.get('move_order');
  const { push, pop, beginDrilldown } = useBreadcrumbs();

  const [orders, setOrders] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [showAliases, setShowAliases] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showDelDialog, setShowDelDialog] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [reinitOrder, setReinitOrder] = useState(null);
  const [editOrderId, setEditOrderId] = useState(null);
  const [openedFromParam, setOpenedFromParam] = useState(false);
  const canManageMoveOrders  = usePermission('manage_move_orders');
  const canViewSupplierPay   = usePermission('record_supplier_payments');

  const reloadOrders = () => {
    setLoading(true);
    axiosInstance
      .get('/api/inventory/move-order/', {
        params: { ...queryParams, approvals_only: isApprovalsView ? '1' : undefined },
      })
      .then((res) => {
        setOrders(res.data.results || []);
        setTotalRecords(res.data.count || 0);
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
        const loadMOFromNav = async () => {
            if (!location.state?.moId) return;
            try {
                openDetail(location.state.moId);
            } catch (err) {
                handleApiError(err);
            }
        };

      loadMOFromNav();
  }, [location.state?.invId]);

  useEffect(() => {
    reloadOrders();
  }, [isApprovalsView, queryParams]);

  useEffect(() => {
    if (location.state?.openCreate && canManageMoveOrders) {
      setShowPurchase(true);
    }
  }, [location.state?.openCreate, canManageMoveOrders]);

  useEffect(() => {
    const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
    setIsApprover(userSettings?.isApprover);
  }, []);

  useEffect(() => {
    if (!focusMoveOrderId) return;      // no id in URL
    if (openedFromParam) return;        // already opened once
    // Open directly by id — MoveOrderDetailDialog fetches its own data, so this
    // doesn't depend on the id being present in the current (paginated) page.
    openDetail(focusMoveOrderId);
    setOpenedFromParam(true);
  }, [focusMoveOrderId, openedFromParam]);

  const handleReinitiateFromDetail = (order) => {
    if (!order) return;
    setDetailVisible(false);
    setReinitOrder(order);
    setShowPurchase(true);
  };

  const handleContinueDraft = (row) => {
    setEditOrderId(row.id);
    setShowPurchase(true);
  };

  const openDetail = (id) => {
    setSelectedOrderId(id);
    setDetailVisible(true);
  };

  const approveOrder = async (row) => {
    try {
      await axiosInstance.post(`/api/inventory/move-order/${row.id}/approve/`);
      handleApiSuccess('Move order approved');
      reloadOrders();
    } catch (e) {
      handleApiError(e);
    }
  };

  const rejectOrder = async (row) => {
    try {
      await axiosInstance.post(`/api/inventory/move-order/${row.id}/reject/`);
      handleApiSuccess('Move order rejected');
      reloadOrders();
    } catch (e) {
      handleApiError(e);
    }
  };

  const columns = [
    { Header: 'Order Number', accessor:'order_number', filtered: true,
      bodyTemplate: (row) => (
            <span
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={(e) => {
                  beginDrilldown();
                  e.preventDefault();
                  openDetail(row.id);
                  push({
                        label: `MO: ${row.order_number}`,
                    });
                }}
            >
                {row.order_number}
            </span>
            )
    },
    { Header: 'From Department', accessor: 'source_department_name'},
    { Header: 'To Department', accessor: 'destination_department_name' },
    {
      Header: 'Type',
      accessor: 'type',
      filtered: true,
      bodyTemplate: (row) => {
        let sev = 'info';
        let label = row.type;

        switch (row.type) {
          case 'NEW':
            sev = 'success';
            label = 'New (Purchase)';
            break;
          case 'DELETE':
            sev = 'danger';
            label = 'Delete (Stock Out)';
            break;
          case 'TRANSFER':
            sev = 'warning';
            label = 'Transfer';
            break;
          default:
            sev = 'info';
            label = row.type;
        }

        return <Tag value={label} severity={sev} />;
      },
    },
    {
      Header: 'Status',
      accessor: 'status_display',
      filtered: true,
      bodyTemplate: (row) => {
        const sev =
          row.status_display === 'Completed'
            ? 'success'
            : row.status_display === 'Cancelled'
            ? 'danger'
            : 'info';
        return <Tag value={row.status_display} severity={sev} />;
      },
    },
    { Header: 'Pay Status', accessor: 'payment_status_display', filtered: true },
    {
      Header: 'Actions',
      accessor: 'actions',
      bodyTemplate: (row) => {
        // backend allows approve for these statuses
        const canApproveReject = isApprover && row.status === 'pending_approval';
        const canContinueDraft = canManageMoveOrders && row.status === 'draft' && !isApprovalsView;
        if (!canApproveReject && !canContinueDraft) return null;

        return (
          <div className="flex gap-2">
            {canContinueDraft && (
              <Button
                icon="pi pi-pencil"
                className="p-button-info p-button-sm"
                rounded
                text
                tooltip="Continue editing this draft"
                onClick={() => handleContinueDraft(row)}
              />
            )}
            {canApproveReject && (
              <>
                <Button
                  icon="pi pi-check"
                  className="p-button-success p-button-sm"
                  rounded
                  text
                  tooltip="Approve"
                  onClick={() => approveOrder(row)}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-danger p-button-sm"
                  rounded
                  text
                  tooltip="Reject"
                  onClick={() => rejectOrder(row)}
                />
              </>
            )}
          </div>
        );
      },
    },
    { Header: 'Direct GRN', accessor: 'is_direct_grn', filtered: true,
        bodyTemplate: (rowData) => (
            <Checkbox checked={rowData.is_direct_grn === true || rowData.is_direct_grn === 'Y'} disabled />
        ), },
    { Header: 'Supplier', accessor: 'supplier_name' },
    { Header: 'On Credit', accessor: 'receive_on_credit', filtered: true,
        bodyTemplate: (rowData) => (
            <Checkbox checked={rowData.receive_on_credit === true || rowData.receive_on_credit === 'Y'} disabled />
        ), },
    { Header: 'Total', accessor: 'total_purchase_price' },
    { Header: 'Paid', accessor: 'total_paid' },
    { Header: 'Payment Due', accessor: 'invoice_due_date', sortable: true },
    { Header: 'Created By', accessor: 'created_by_name' },
    { Header: 'Approved By', accessor: 'approved_by_name' },
    { Header: 'Created At', accessor: 'created_at', dataType: 'date' },
  ];

    const headerAction = !isApprovalsView && canManageMoveOrders ? (
        <>
        <Button type="button" icon="pi pi-cart-plus" label="Purchase Order" outlined onClick={() => setShowPurchase(true)} className="w-full sm:w-auto" />
        <Button type="button" icon="pi pi-camera" label="Scan Bill" outlined onClick={() => setShowScan(true)} className="w-full sm:w-auto" />
        <Button type="button" icon="pi pi-link" label="Bill names" outlined onClick={() => setShowAliases(true)} className="w-full sm:w-auto" tooltip="How suppliers word your products on their bills" />
        <Button type="button" label="Transfer Order" icon="pi pi-cart-arrow-down" outlined className="w-full sm:w-auto" onClick={() => setShowMoveDialog(true)} />
        <Button type="button" icon="pi pi-cart-minus" label="Delete Order" outlined onClick={() => setShowDelDialog(true)} className="w-full sm:w-auto" />
        </>
    ) : null;
  
  return (
    <div>
        <div className="containers">
            <GlobalTable
            columns={columns}
            data={orders}
            loading={loading}
            tabletitle={isApprovalsView ? "Move Order Approvals" : "Move Orders List"}
            showEditColumn={false}
            lazy
            totalRecords={totalRecords}
            lazyParams={lazyParams}
            onPage={onPage}
            onSort={onSort}
            onFilter={onFilter}
            headerAction={headerAction}
            />
        </div>

        <MoveOrderDetailDialog
            orderId={selectedOrderId}
            visible={detailVisible}
            onHide={() => {setDetailVisible(false); pop();}}
            onReinitiate={handleReinitiateFromDetail}
            onChanged={reloadOrders}
            canManageMoveOrders={canManageMoveOrders}  
            canRecordPayments={canViewSupplierPay}
        />

        <MoveOrderPurchaseDialog
            visible={showPurchase}
            onHide={() => {setShowPurchase(false); setReinitOrder(null); setEditOrderId(null);}}
            onPurchased={() => { reloadOrders(); }}
            reinitOrder={reinitOrder}
            editOrderId={editOrderId}
        />
        <PurchaseScanDialog visible={showScan} onHide={() => setShowScan(false)} onCreated={reloadOrders} />
        <SupplierAliasesDialog visible={showAliases} onHide={() => setShowAliases(false)} />
        <MoveOrderDialog 
            visible={showMoveDialog} 
            onHide={() => setShowMoveDialog(false)} 
            onSaved={reloadOrders}
        />
        <MoveOrderDeleteDialog
            visible={showDelDialog} 
            onHide={() => setShowDelDialog(false)} 
            onSaved={reloadOrders}
        />
    </div>
  );
}